import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import { post } from '@rails/request.js'

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}

function extractFiles(dataTransfer) {
  const items = Array.from(dataTransfer?.items || [])
  const files = Array.from(dataTransfer?.files || []).filter(
    (file) => file instanceof File,
  )

  return {
    files,
    foldersIgnored: items.filter(
      (item) => item.webkitGetAsEntry?.()?.isDirectory,
    ).length,
  }
}

export default class extends Controller {
  static targets = [
    'dropzone',
    'fileInput',
    'notices',
    'noticesList',
    'progressBar',
    'progressHint',
    'progressText',
  ]

  static values = {
    acceptedContentTypes: String,
    directUploadUrl: String,
    importItemsUrl: String,
    maxConcurrency: Number,
    maxPdfSizeMb: Number,
    maxZipSizeGb: Number,
    zipExtractionUrl: String,
  }

  connect() {
    this.acceptedTypes = this.acceptedContentTypesValue.split(',')
    this.activeUploads = 0
    this.completedUploads = 0
    this.createRequestsInFlight = 0
    this.dragCounter = 0
    this.pendingQueue = []
    this.totalFiles = 0

    this.element.addEventListener('dragenter', this.#handleDragEnter.bind(this))
    this.element.addEventListener('dragleave', this.#handleDragLeave.bind(this))
    this.element.addEventListener('dragover', this.#handleDragOver.bind(this))
    this.element.addEventListener('drop', this.#handleDrop.bind(this))
    this.#bindBeforeUnload()
    this.#bindTurboNavigationGuard()
    this.#updateProgressUi()
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
  }

  handleFileSelect(event) {
    const { target } = event
    const files = Array.from(target.files)
    const validFiles = this.#validateFiles(files)

    if (validFiles.length === 0) return

    this.totalFiles += validFiles.length
    this.#updateProgressUi()

    this.pendingQueue.push(...validFiles)
    this.#drainQueue()

    target.value = ''
  }

  openFileDialog() {
    this.fileInputTarget.click()
  }

  #activateDropzone() {
    this.dropzoneTarget.classList.add('!border-brand-500', '!bg-brand-50')
  }

  #addNotice(message) {
    if (!this.hasNoticesTarget || !this.hasNoticesListTarget) return

    const li = document.createElement('li')
    li.textContent = message
    this.noticesListTarget.prepend(li)
  }

  #bindBeforeUnload() {
    this.beforeUnloadHandler = (event) => {
      if (this.activeUploads > 0) {
        event.preventDefault()

        const e = event
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  #bindTurboNavigationGuard() {
    this.turboBeforeVisitHandler = (event) => {
      if (this.activeUploads > 0) {
        // eslint-disable-next-line no-alert, no-restricted-globals
        const proceed = confirm(
          'Uploads are still in progress. Are you sure you want to leave?',
        )

        if (!proceed) {
          event.preventDefault()
        }
      }
    }

    document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
  }

  async #createImportItem(blobSignedId) {
    this.createRequestsInFlight += 1

    const response = await fetch(this.importItemsUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({
        import_item: { blob_signed_id: blobSignedId },
      }),
      credentials: 'same-origin',
    })

    this.createRequestsInFlight -= 1

    if (!response.ok) {
      throw new Error('Import item creation failed')
    }
  }

  #deactivateDropzone() {
    this.dropzoneTarget.classList.remove('!border-brand-500', '!bg-brand-50')
  }

  #directUpload(file) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.directUploadUrlValue)

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob)
        }
      })
    })
  }

  #drainQueue() {
    while (
      this.activeUploads < this.maxConcurrencyValue && this.pendingQueue.length > 0
    ) {
      const file = this.pendingQueue.shift()
      this.#startUpload(file)
    }
  }

  #handleDragEnter(event) {
    event.preventDefault()

    this.dragCounter += 1
    this.#activateDropzone()
  }

  #handleDragLeave(event) {
    event.preventDefault()

    this.dragCounter -= 1
    if (this.dragCounter === 0) {
      this.#deactivateDropzone()
    }
  }

  // eslint-disable-next-line class-methods-use-this
  #handleDragOver(event) {
    event.preventDefault()

    const { dataTransfer } = event
    if (dataTransfer) {
      dataTransfer.dropEffect = 'copy'
    }
  }

  #handleDrop(event) {
    event.preventDefault()
    this.dragCounter = 0
    this.#deactivateDropzone()
    const { files, foldersIgnored } = extractFiles(event.dataTransfer)

    if (foldersIgnored > 0) {
      this.#addNotice(
        `Folders are not supported. Ignored ${foldersIgnored} folder${foldersIgnored === 1 ? '' : 's'}.`,
      )
    }

    if (files.length === 0) return

    const validFiles = this.#validateFiles(files)
    if (validFiles.length === 0) return

    this.totalFiles += validFiles.length
    this.#updateProgressUi()

    this.pendingQueue.push(...validFiles)
    this.#drainQueue()
  }

  async #startUpload(file) {
    this.activeUploads += 1

    try {
      const blob = await this.#directUpload(file)
      if (file.type === 'application/zip') {
        await this.#startZipExtraction(blob.signed_id)
        this.#addNotice(
          `${file.name} uploaded. The extraction will be handled in the \
          background. You will receive a notification when the extraction \
          completes.`,
        )
      } else {
        await this.#createImportItem(blob.signed_id)
      }
      this.completedUploads += 1
    } catch (error) {
      this.#addNotice(`Upload failed for "${file.name}".`)
    }

    this.activeUploads -= 1
    this.#updateProgressUi()
    this.#drainQueue()
  }

  async #startZipExtraction(blobSignedId) {
    this.createRequestsInFlight += 1

    const response = await post(
      this.zipExtractionUrlValue,
      {
        body: { blob: { blob_signed_id: blobSignedId } },
        responseKind: 'json',
      },
    )

    this.createRequestsInFlight -= 1

    if (!response.ok) {
      throw new Error('Import item creation failed')
    }
  }

  #updateProgressUi() {
    const total = this.totalFiles
    const complete = this.completedUploads

    this.progressTextTarget.textContent = `${complete} / ${total}`

    const percent = total === 0 ? 0 : Math.round((complete / total) * 100)
    this.progressBarTarget.style.width = `${percent}%`

    if (total === 0) {
      this.progressHintTarget.textContent = 'Drop files to begin'
    } else if (complete < total) {
      this.progressHintTarget.textContent = 'Uploading...'
    } else {
      this.progressHintTarget.textContent = 'All files uploaded'
    }
  }

  #validateFiles(files) {
    const maxPdfBytes = this.maxPdfSizeMbValue * 1024 * 1024
    const maxZipBytes = this.maxZipSizeGbValue * 1024 * 1024 * 1024
    const valid = []

    files.forEach((file) => {
      if (!this.acceptedTypes.includes(file.type)) {
        this.#addNotice(`"${file.name}" is not a PDF and was ignored.`)
        return
      }

      if (file.type === 'application/pdf' && file.size > maxPdfBytes) {
        this.#addNotice(`"${file.name}" exceeds 20MB and was ignored.`)
        return
      }

      if (file.type === 'application/zip' && file.size > maxZipBytes) {
        this.#addNotice(`"${file.name}" exceeds 4GB and was ignored.`)
        return
      }

      const sanitizedFile = new File(
        [file],
        Valence.Utils.File.sanitizeFilename(file.name),
        { type: file.type },
      )
      valid.push(sanitizedFile)
    })

    return valid
  }
}
